/**
 * Industry Template Selector
 * Step 1 of onboarding - choose industry template for quick setup
 */

import React from 'react';
import { IndustryTemplate, useOnboarding } from '../contexts/OnboardingContext';
import { Card } from './ui';

interface Template {
  id: IndustryTemplate;
  name: string;
  icon: string;
  description: string;
  features: string[];
  recommended: {
    b3trPerDollar: number;
    minRedemption: number;
    ecoBonus: string;
  };
}

const TEMPLATES: Template[] = [
  {
    id: 'cafe',
    name: 'Coffee Shop',
    icon: '☕',
    description: 'Perfect for cafes and coffee shops',
    features: ['10 Stars per $1', 'Mugshot integration', 'Lower tier thresholds'],
    recommended: {
      b3trPerDollar: 10,
      minRedemption: 100,
      ecoBonus: 'Bring reusable cup',
    },
  },
  {
    id: 'restaurant',
    name: 'Restaurant',
    icon: '🍽️',
    description: 'Optimized for dining establishments',
    features: ['10 Stars per $1', 'Greencart integration', 'Medium tier thresholds'],
    recommended: {
      b3trPerDollar: 10,
      minRedemption: 200,
      ecoBonus: 'Farm-to-table',
    },
  },
  {
    id: 'retail',
    name: 'Retail Store',
    icon: '🛍️',
    description: 'Great for shopping and merchandise',
    features: ['5 Stars per $1', 'BYB integration', 'Higher tier thresholds'],
    recommended: {
      b3trPerDollar: 5,
      minRedemption: 500,
      ecoBonus: 'Bring your bag',
    },
  },
  {
    id: 'salon',
    name: 'Salon & Spa',
    icon: '💆',
    description: 'Tailored for beauty and wellness',
    features: ['15 Stars per $1', 'BYB integration', 'Luxury tier structure'],
    recommended: {
      b3trPerDollar: 15,
      minRedemption: 150,
      ecoBonus: 'Eco-friendly products',
    },
  },
  {
    id: 'fitness',
    name: 'Fitness Center',
    icon: '💪',
    description: 'Designed for gyms and studios',
    features: ['20 Stars per $1', 'BYB integration', 'High engagement tiers'],
    recommended: {
      b3trPerDollar: 20,
      minRedemption: 200,
      ecoBonus: 'Sustainable gear',
    },
  },
  {
    id: 'custom',
    name: 'Custom Setup',
    icon: '⚙️',
    description: 'Build your own from scratch',
    features: ['Full customization', 'No presets', 'Flexible configuration'],
    recommended: {
      b3trPerDollar: 10,
      minRedemption: 100,
      ecoBonus: 'Your choice',
    },
  },
];

export const IndustryTemplateSelector: React.FC = () => {
  const { data, applyTemplate, updateData: _updateData } = useOnboarding();

  const handleSelectTemplate = (templateId: IndustryTemplate) => {
    applyTemplate(templateId);
  };

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-h2 font-bold text-black mb-2">Choose Your Industry</h2>
        <p className="text-base text-black-lighter">
          Select a template to get started quickly with recommended settings for your business type
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {TEMPLATES.map((template) => {
          const isSelected = data.industry === template.id;

          return (
            <button
              key={template.id}
              onClick={() => handleSelectTemplate(template.id)}
              className="text-left transition-all duration-200"
            >
              <Card
                className={`h-full hover:shadow-lg transition-all duration-200 ${
                  isSelected
                    ? 'ring-2 ring-orange-400 bg-orange-50'
                    : 'hover:border-orange-200'
                }`}
              >
                <div className="p-6">
                  {/* Icon and Name */}
                  <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-3">
                      <div className="text-4xl">{template.icon}</div>
                      <div>
                        <h3 className="text-base font-semibold text-black">{template.name}</h3>
                        {isSelected && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-orange-400 text-white text-xs font-semibold rounded mt-1">
                            <svg className="w-3 h-3" fill="currentColor" viewBox="0 0 20 20">
                              <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                            </svg>
                            Selected
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Description */}
                  <p className="text-sm text-black-lighter mb-4">{template.description}</p>

                  {/* Features */}
                  <div className="space-y-2 mb-4">
                    {template.features.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 mt-0.5 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                        <span className="text-sm text-black">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {/* Recommended Settings Preview */}
                  <div className="pt-4 border-t border-border-light">
                    <p className="text-xs font-semibold text-black-lighter mb-2">Recommended:</p>
                    <div className="grid grid-cols-2 gap-2 text-xs">
                      <div>
                        <p className="text-black-lighter">Earn Rate:</p>
                        <p className="font-semibold text-black">{template.recommended.b3trPerDollar} Stars/$1</p>
                      </div>
                      <div>
                        <p className="text-black-lighter">Min Redeem:</p>
                        <p className="font-semibold text-black">{template.recommended.minRedemption} Stars</p>
                      </div>
                    </div>
                    <div className="mt-2">
                      <p className="text-black-lighter">Eco Bonus:</p>
                      <p className="font-semibold text-black">{template.recommended.ecoBonus}</p>
                    </div>
                  </div>
                </div>
              </Card>
            </button>
          );
        })}
      </div>

      {/* Info Banner */}
      {data.industry !== 'custom' && (
        <div className="mt-8 p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-3">
            <svg className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div>
              <p className="text-sm font-semibold text-blue-900">Template Selected</p>
              <p className="text-sm text-blue-800 mt-1">
                These settings are optimized for {TEMPLATES.find(t => t.id === data.industry)?.name.toLowerCase()}. You can customize everything in the next steps.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IndustryTemplateSelector;
